import { Key } from "../enums";
import type { AnalyticsData } from "../../types/data";
import type { ReactNode } from "react";
import {
  renderDateRangeFilter,
  renderMultiSelectFilter,
  renderBucketFilter,
} from "../../renderers";
import { RANGE_DEFINITIONS } from "../../service/dateRange";

export type ColumnId = Key;

export type DateRange = {
  from: string;
  to: string;
  // Id of the relative preset (RangeDefinition.value) this range was resolved
  // from. Present → the URL keeps the preset and re-resolves it against today;
  // absent → a fixed, hand-picked range.
  preset?: string;
};

export type DatePreset = {
  value: string;
  label: string;
  getRange: () => [Date, Date];
};

export type AppliedFilters = {
  [Key.STUDENT_NAME]?: string[];
  [Key.LESSON_NAME]?: string[];
  [Key.CLASSROOM_NAME]?: string[];
  [Key.LAST_LEARNED]?: DateRange;
  includeNotStarted?: boolean;
  [Key.TIME_SPENT]?: string[];
  [Key.GOALS]?: string[];
};

export type FilterConfig = {
  key: ColumnId;
  label: string;
  extractValue?: (row: AnalyticsData) => string | number | null;
  // Present only when the stored value differs from the text shown to the user,
  // i.e. the filter is keyed on an id. Drives label lookup and legacy matching,
  // so "id-valued" stays a property of the filter rather than a separate list.
  extractLabel?: (row: AnalyticsData) => string;
  formatMultipleTitle?: (count: number) => string;
  render: (props: {
    data: AnalyticsData[];
    value: AppliedFilters[keyof AppliedFilters] | undefined;
    onChange: (value: AppliedFilters[keyof AppliedFilters] | undefined) => void;
    onApply: () => void;
    onClear: () => void;
    nameHint?: string;
    // The last applied (not the uncommitted, in-dropdown) value. Used to decide
    // which deep-linked ids to keep listed even after the user unticks them,
    // so the row survives until Apply rather than vanishing mid-edit.
    committedValue?: AppliedFilters[keyof AppliedFilters];
    // Filter-specific options the dropdown owns, placed before the footer so
    // reading order and Tab order agree. Only the date filter uses it.
    beforeActions?: ReactNode;
  }) => ReactNode;
};

export type FilterOption = {
  value: string;
  label: string;
};

type NamedKey = Key.STUDENT_NAME | Key.LESSON_NAME | Key.CLASSROOM_NAME;

// One pass, no sort: the row cell already carries both halves, so an id-valued
// filter needs no special casing here.
export const labelByValue = (
  data: AnalyticsData[],
  key: NamedKey,
): Map<string, string> => {
  const byValue = new Map<string, string>();
  data.forEach((row) => {
    const cell = row[key];
    const value = cell.value || cell.label;
    if (value && !byValue.has(value)) byValue.set(value, cell.label);
  });
  return byValue;
};

export const extractUniqueValues = (
  data: AnalyticsData[],
  key: NamedKey,
): FilterOption[] =>
  Array.from(labelByValue(data, key), ([value, label]) => ({
    value,
    label,
  })).sort((a, b) => a.label.localeCompare(b.label));

// Driven by the config's accessors rather than a key, so it needs no cast and
// picks up any filter that later becomes id-valued.
export const resolveOptionLabels = (
  data: AnalyticsData[],
  config: Pick<FilterConfig, "extractValue" | "extractLabel">,
  values: string[],
  nameHint?: string,
): string[] => {
  const { extractValue, extractLabel } = config;
  if (!extractValue || !extractLabel) return values;

  const labels = new Map<string, string>();
  data.forEach((row) => {
    const id = String(extractValue(row) ?? "");
    if (id && !labels.has(id)) labels.set(id, extractLabel(row));
  });

  return values.map((value) => {
    const label = labels.get(value);
    if (label) return label;
    // A single deep-linked selection can carry its own name, for a group or
    // lesson that has no sessions yet and so appears in no row.
    if (nameHint && values.length === 1) return nameHint;
    return value;
  });
};

export const TIME_SPENT_BUCKETS = [
  "Less than 5 min",
  "5-10 min",
  "10-20 min",
  "20-30 min",
  "30 and above",
];

const getTimeSpentBucket = (timeSpentMs: number): string => {
  const minutes = Math.floor(timeSpentMs / 60000);
  if (minutes < 5) return "Less than 5 min";
  if (minutes < 10) return "5-10 min";
  if (minutes < 20) return "10-20 min";
  if (minutes < 30) return "20-30 min";
  return "30 and above";
};

/**
 * Lessons made only of assessment slides carry no content bites, so they have
 * no completion percentage at all. They are not 0% — folding them into that
 * bucket hid every such lesson, because the default filter drops 0%.
 */
export const NO_GOALS_BUCKET = "No goals";

export const GOAL_BUCKETS = [
  "0%",
  "1-20%",
  "20-40%",
  "40-60%",
  "60-70%",
  "70-80%",
  "80-85%",
  "85-90%",
  "90-95%",
  "95-100%",
  NO_GOALS_BUCKET,
];

const getGoalBucket = (row: AnalyticsData): string => {
  const { goalsMet, totalGoals } = row;
  if (totalGoals === 0) return NO_GOALS_BUCKET;
  const percentage = Math.round((goalsMet / totalGoals) * 100);
  if (percentage === 0) return "0%";
  if (percentage <= 20) return "1-20%";
  if (percentage <= 40) return "20-40%";
  if (percentage <= 60) return "40-60%";
  if (percentage <= 70) return "60-70%";
  if (percentage <= 80) return "70-80%";
  if (percentage <= 85) return "80-85%";
  if (percentage <= 90) return "85-90%";
  if (percentage <= 95) return "90-95%";
  return "95-100%";
};

export const filterConfigs: FilterConfig[] = [
  {
    key: Key.STUDENT_NAME,
    label: "Students",
    extractValue: (row) => row[Key.STUDENT_NAME].label,
    formatMultipleTitle: (count) => `${count} Students`,
    render: (props) =>
      renderMultiSelectFilter({
        ...props,
        getOptions: (data) => extractUniqueValues(data, Key.STUDENT_NAME),
        searchPlaceholder: "Search for a Student",
        showSearch: true,
      }),
  },
  {
    key: Key.LESSON_NAME,
    label: "Lessons",
    extractValue: (row) => row[Key.LESSON_NAME].value,
    extractLabel: (row) => row[Key.LESSON_NAME].label,
    formatMultipleTitle: (count) => `${count} Lessons`,
    render: (props) =>
      renderMultiSelectFilter({
        ...props,
        getOptions: (data) => extractUniqueValues(data, Key.LESSON_NAME),
        searchPlaceholder: "Search for a Lesson",
        showSearch: true,
      }),
  },
  {
    key: Key.CLASSROOM_NAME,
    label: "Groups",
    extractValue: (row) => row[Key.CLASSROOM_NAME].value,
    extractLabel: (row) => row[Key.CLASSROOM_NAME].label,
    formatMultipleTitle: (count) => `${count} Groups`,
    render: (props) =>
      renderMultiSelectFilter({
        ...props,
        getOptions: (data) => extractUniqueValues(data, Key.CLASSROOM_NAME),
        searchPlaceholder: "Search for a Group",
        showSearch: true,
      }),
  },
  {
    key: Key.LAST_LEARNED,
    label: "Last Learned",
    extractValue: (row) => row[Key.LAST_LEARNED] ?? null,
    formatMultipleTitle: (count) => `${count} Dates`,
    render: (props) =>
      renderDateRangeFilter({
        ...props,
        presets: DATE_PRESETS,
      }),
  },
  {
    key: Key.TIME_SPENT,
    label: "Time spent (min)",
    extractValue: (row) => getTimeSpentBucket(row[Key.TIME_SPENT]),
    formatMultipleTitle: (count) => `${count} Time ranges`,
    render: (props) =>
      renderBucketFilter({
        ...props,
        buckets: TIME_SPENT_BUCKETS,
      }),
  },
  {
    key: Key.GOALS,
    label: "Completion %",
    extractValue: (row) => getGoalBucket(row),
    render: (props) =>
      renderBucketFilter({
        ...props,
        buckets: GOAL_BUCKETS,
      }),
  },
];

export const getFilterConfig = (key: ColumnId): FilterConfig | undefined => {
  return filterConfigs.find((config) => config.key === key);
};

export const DATE_PRESETS: DatePreset[] = RANGE_DEFINITIONS;
